import { useState, useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useSEO } from '../../utils/seo';
import { useNavigate } from 'react-router-dom';
import MobileStickyCTA from '@/components/feature/MobileStickyCTA';

export default function AddictionServices() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilltreatment.com';

  useSEO({
    title: 'Addiction Treatment Thornhill | Methadone & Suboxone Clinic',
    description: 'Leading addiction pharmacy and clinic in Thornhill serving Richmond Hill, Vaughan, and North York. Expert methadone pharmacy services, Suboxone clinic programs, Kadian treatment, alcohol addiction support, and smoking cessation. Professional addiction medicine with compassionate care. Call (905) 709-9985 for confidential consultation.',
    keywords: 'addiction pharmacy Thornhill, addiction clinic Thornhill, methadone pharmacy Thornhill, Suboxone clinic Thornhill, methadone treatment Richmond Hill, Suboxone pharmacy Vaughan, addiction clinic North York, opioid treatment pharmacy, methadone maintenance Thornhill, Suboxone therapy Richmond Hill, Kadian pharmacy, alcohol addiction clinic, smoking cessation pharmacy, addiction medicine Thornhill, opioid replacement therapy clinic, addiction recovery pharmacy Thornhill, methadone clinic near me, Suboxone doctor Thornhill',
    canonical: `${siteUrl}/addiction-services`,
    ogType: 'website',
    schema: {
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      "name": "Thornhill Drug-Mart Addiction Pharmacy & Clinic",
      "description": "Premier addiction pharmacy and clinic in Thornhill, Ontario providing comprehensive methadone pharmacy services, Suboxone clinic programs, Kadian treatment, alcohol use disorder treatment, and smoking cessation services. Serving Thornhill, Richmond Hill, Vaughan, North York, and Greater Toronto Area with expert addiction medicine and compassionate care.",
      "url": `${siteUrl}/addiction-services`,
      "telephone": "+1-905-709-9985",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "8108 Yonge St Unit 3-4",
        "addressLocality": "Thornhill",
        "addressRegion": "ON",
        "postalCode": "L4J 1W4",
        "addressCountry": "CA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "43.8184",
        "longitude": "-79.4278"
      },
      "medicalSpecialty": ["Addiction Medicine", "Pharmacy"],
      "availableService": [
        {
          "@type": "MedicalTherapy",
          "name": "Methadone Pharmacy Services",
          "description": "Professional methadone pharmacy with daily supervised dosing, individualized treatment plans, and comprehensive medical monitoring for opioid addiction recovery in Thornhill, Richmond Hill, Vaughan, and North York"
        },
        {
          "@type": "MedicalTherapy",
          "name": "Suboxone Clinic Programs",
          "description": "Expert Suboxone clinic offering buprenorphine-naloxone therapy with flexible dosing schedules and reduced clinic visit requirements for qualified patients across Thornhill and surrounding areas"
        },
        {
          "@type": "MedicalTherapy",
          "name": "Kadian Treatment Pharmacy",
          "description": "Long-acting morphine sulfate pharmacy services for chronic pain management and addiction treatment with extended-release medication for 24-hour control"
        },
        {
          "@type": "MedicalTherapy",
          "name": "Alcohol Addiction Clinic",
          "description": "Comprehensive alcohol use disorder treatment clinic with medication-assisted therapy, withdrawal management, and relapse prevention support"
        },
        {
          "@type": "MedicalTherapy",
          "name": "Smoking Cessation Pharmacy",
          "description": "Professional smoking cessation pharmacy services with nicotine replacement therapy, prescription medications, and behavioral support strategies"
        }
      ],
      "areaServed": [
        {
          "@type": "City",
          "name": "Thornhill",
          "containedInPlace": {
            "@type": "AdministrativeArea",
            "name": "Ontario"
          }
        },
        {
          "@type": "City",
          "name": "Richmond Hill",
          "containedInPlace": {
            "@type": "AdministrativeArea",
            "name": "Ontario"
          }
        },
        {
          "@type": "City",
          "name": "Vaughan",
          "containedInPlace": {
            "@type": "AdministrativeArea",
            "name": "Ontario"
          }
        },
        {
          "@type": "City",
          "name": "North York",
          "containedInPlace": {
            "@type": "AdministrativeArea",
            "name": "Ontario"
          }
        },
        {
          "@type": "City",
          "name": "Toronto",
          "containedInPlace": {
            "@type": "AdministrativeArea",
            "name": "Ontario"
          }
        }
      ],
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Monday",
          "opens": "08:00",
          "closes": "12:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Tuesday", "Wednesday", "Thursday"],
          "opens": "08:00",
          "closes": "20:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Friday",
          "opens": "08:00",
          "closes": "14:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Saturday",
          "opens": "09:00",
          "closes": "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Sunday",
          "opens": "10:00",
          "closes": "17:00"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Addiction Pharmacy & Clinic Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Methadone Pharmacy Thornhill",
              "description": "Professional methadone pharmacy services with supervised dosing and medical monitoring"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Suboxone Clinic Thornhill",
              "description": "Expert Suboxone clinic with flexible treatment programs and ongoing medical supervision"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Addiction Pharmacy Services",
              "description": "Comprehensive addiction pharmacy services including Kadian, alcohol treatment, and smoking cessation"
            }
          }
        ]
      }
    }
  });

  const slides = [
    {
      image: 'https://readdy.ai/api/search-image?query=compassionate%20healthcare%20professional%20in%20modern%20medical%20consultation%20room%20with%20warm%20lighting%2C%20supportive%20addiction%20treatment%20environment%2C%20professional%20counseling%20setting%2C%20clean%20teal%20and%20white%20colors%2C%20hopeful%20recovery%20atmosphere%2C%20private%20consultation%20space%2C%20medical%20expertise%20and%20care&width=1920&height=800&seq=addiction-slide-1&orientation=landscape',
      title: 'Thornhill Addiction Services',
      description: 'Compassionate, evidence-based addiction treatment programs in Thornhill'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=professional%20medical%20team%20in%20modern%20addiction%20treatment%20clinic%2C%20supportive%20healthcare%20environment%2C%20clean%20bright%20facility%20with%20teal%20accents%2C%20medication-assisted%20treatment%20setting%2C%20recovery-focused%20atmosphere%2C%20professional%20medical%20staff%2C%20welcoming%20healthcare%20space&width=1920&height=800&seq=addiction-slide-2&orientation=landscape',
      title: 'Expert Addiction Treatment',
      description: 'Methadone, Suboxone, and Kadian programs with dedicated medical support'
    },
    {
      image: 'https://readdy.ai/api/search-image?query=peaceful%20recovery%20journey%20concept%20with%20sunrise%20over%20calm%20landscape%2C%20hope%20and%20healing%20symbolism%2C%20new%20beginning%20atmosphere%2C%20soft%20teal%20and%20warm%20colors%2C%20inspirational%20healthcare%20imagery%2C%20path%20to%20wellness%2C%20serene%20medical%20environment&width=1920&height=800&seq=addiction-slide-3&orientation=landscape',
      title: 'Your Recovery Journey Starts Here',
      description: 'Personalized treatment plans for lasting recovery and wellness'
    }
  ];

  const treatmentPrograms = [
    {
      icon: 'ri-medicine-bottle-line',
      title: 'Methadone Maintenance Treatment (MMT)',
      description: 'Evidence-based opioid replacement therapy using methadone to help manage withdrawal symptoms and reduce cravings. Our <strong>methadone pharmacy</strong> in <strong>Thornhill</strong> provides daily supervised dosing with comprehensive medical monitoring, serving patients from <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>.',
      features: [
        'Daily supervised dosing at our Thornhill methadone pharmacy',
        'Individualized treatment plans',
        'Medical monitoring and support',
        'Counseling referrals',
        'Take-home privileges available',
        'Long-term recovery support'
      ],
      benefits: 'Reduces withdrawal symptoms, decreases drug cravings, and helps patients regain stability in their lives.',
      color: 'teal',
      image: 'https://readdy.ai/api/search-image?query=professional%20medical%20methadone%20treatment%20clinic%20interior%20with%20modern%20medication%20dispensing%20area%2C%20clean%20healthcare%20environment%20with%20teal%20accents%2C%20supervised%20dosing%20station%2C%20compassionate%20addiction%20medicine%20setting%2C%20bright%20natural%20lighting%2C%20professional%20pharmaceutical%20care%20space%20with%20organized%20medical%20supplies&width=800&height=600&seq=methadone-treatment-001&orientation=landscape',
      link: '/methadone-treatment'
    },
    {
      icon: 'ri-capsule-line',
      title: 'Suboxone Maintenance Treatment',
      description: 'Buprenorphine-naloxone combination therapy for <strong>opioid use disorder treatment</strong>. Our <strong>Suboxone clinic</strong> in <strong>Thornhill</strong> offers flexible dosing schedules and reduced clinic visit requirements for qualified patients from <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>.',
      features: [
        'Flexible dosing schedules at our Suboxone clinic',
        'Reduced clinic visits',
        'Lower abuse potential',
        'Effective withdrawal management',
        'Prescription refills available',
        'Ongoing medical supervision'
      ],
      benefits: 'Provides effective relief from withdrawal symptoms with a lower risk of misuse compared to other treatments.',
      color: 'cyan',
      image: 'https://readdy.ai/api/search-image?query=modern%20suboxone%20treatment%20consultation%20room%20with%20comfortable%20seating%2C%20professional%20medical%20counseling%20space%2C%20warm%20teal%20and%20white%20colors%2C%20private%20healthcare%20setting%2C%20medication-assisted%20treatment%20environment%2C%20supportive%20recovery%20atmosphere%20with%20natural%20lighting%20and%20contemporary%20design&width=800&height=600&seq=suboxone-treatment-001&orientation=landscape',
      link: '/suboxone-treatment'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Kadian Treatment',
      description: 'Long-acting morphine sulfate therapy for chronic pain management and <strong>addiction treatment</strong>. Our <strong>addiction pharmacy</strong> in <strong>Thornhill</strong> provides extended-release Kadian medication for 24-hour pain control, serving the <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong> communities.',
      features: [
        'Extended-release formulation',
        '24-hour pain control',
        'Once or twice daily dosing',
        'Reduced peak-trough fluctuations',
        'Comprehensive pain management',
        'Regular medical assessments'
      ],
      benefits: 'Provides consistent pain relief throughout the day while supporting addiction recovery goals.',
      color: 'teal',
      image: 'https://readdy.ai/api/search-image?query=professional%20pain%20management%20and%20addiction%20treatment%20clinic%20with%20medical%20consultation%20area%2C%20clean%20modern%20healthcare%20facility%20with%20teal%20accents%2C%20chronic%20pain%20therapy%20setting%2C%20extended-release%20medication%20management%20space%2C%20compassionate%20medical%20care%20environment%20with%20organized%20pharmaceutical%20supplies&width=800&height=600&seq=kadian-treatment-001&orientation=landscape',
      link: '/what-is-addiction'
    },
    {
      icon: 'ri-flask-line',
      title: 'Alcohol Use Disorder Treatment',
      description: 'Comprehensive <strong>alcohol addiction treatment</strong> programs including medication-assisted treatment and support services. Our <strong>addiction clinic</strong> team in <strong>Thornhill</strong> provides personalized care for <strong>alcohol use disorder</strong> recovery for patients throughout the Greater Toronto Area.',
      features: [
        'Medication-assisted treatment',
        'Withdrawal management',
        'Craving reduction therapy',
        'Relapse prevention support',
        'Counseling coordination',
        'Long-term recovery planning'
      ],
      benefits: 'Helps manage withdrawal symptoms, reduces alcohol cravings, and supports long-term sobriety.',
      color: 'cyan',
      image: 'https://readdy.ai/api/search-image?query=supportive%20alcohol%20addiction%20treatment%20counseling%20room%20with%20comfortable%20seating%2C%20professional%20recovery%20support%20environment%2C%20warm%20teal%20colors%2C%20private%20medical%20consultation%20space%2C%20medication-assisted%20treatment%20setting%2C%20hopeful%20healing%20atmosphere%20with%20natural%20lighting%20and%20calming%20design&width=800&height=600&seq=alcohol-treatment-001&orientation=landscape',
      link: '/what-is-addiction'
    },
    {
      icon: 'ri-fire-line',
      title: 'Smoking Cessation',
      description: 'Professional <strong>smoking cessation programs</strong> with medication support and behavioral counseling. Our <strong>pharmacy</strong> in <strong>Thornhill</strong> helps you achieve a tobacco-free life with expert guidance and support.',
      features: [
        'Nicotine replacement therapy',
        'Prescription medications',
        'Behavioral support strategies',
        'Personalized quit plans',
        'Follow-up monitoring',
        'Relapse prevention techniques'
      ],
      benefits: 'Increases success rates for quitting smoking and improves overall health outcomes.',
      color: 'teal',
      link: '/pharmacy-services',
      image: 'https://readdy.ai/api/search-image?query=bright%20smoking%20cessation%20support%20clinic%20with%20nicotine%20replacement%20therapy%20products%20display%2C%20professional%20quit%20smoking%20consultation%20area%2C%20clean%20healthcare%20environment%20with%20teal%20accents%2C%20tobacco-free%20wellness%20setting%2C%20motivational%20recovery%20space%20with%20organized%20medical%20supplies%20and%20natural%20lighting&width=800&height=600&seq=smoking-cessation-001&orientation=landscape'
    }
  ];

  const whyChooseUs = [
    {
      icon: 'ri-shield-check-line',
      title: 'Licensed & Accredited',
      description: 'Fully licensed addiction treatment facility with certified medical professionals'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Compassionate Care',
      description: 'Non-judgmental, patient-centered approach to addiction recovery'
    },
    {
      icon: 'ri-time-line',
      title: 'Flexible Hours',
      description: 'Convenient scheduling to accommodate work and family commitments'
    },
    {
      icon: 'ri-team-line',
      title: 'Expert Medical Team',
      description: 'Experienced doctors and pharmacists specializing in addiction medicine'
    },
    {
      icon: 'ri-map-pin-line',
      title: 'Convenient Location',
      description: 'Easily accessible in Thornhill with free parking and transit access'
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Confidential Services',
      description: 'Complete privacy and confidentiality for all patients'
    }
  ];

  const faqs = [
    {
      question: 'What is Methadone Maintenance Treatment (MMT)?',
      answer: 'Methadone Maintenance Treatment is an evidence-based therapy for opioid use disorder. Methadone is a long-acting opioid medication that reduces withdrawal symptoms and cravings without producing the euphoric high of other opioids. Our MMT program in Thornhill includes daily supervised dosing, medical monitoring, and comprehensive support services to help patients achieve long-term recovery.'
    },
    {
      question: 'How does Suboxone treatment differ from Methadone?',
      answer: 'Suboxone (buprenorphine-naloxone) is a partial opioid agonist, while methadone is a full agonist. Suboxone has a lower risk of misuse and overdose, and patients can often take it at home after stabilization. Methadone requires daily clinic visits initially. Both are effective for opioid use disorder treatment, and our doctors will help determine which option is best for your individual needs.'
    },
    {
      question: 'What is Kadian and who is it for?',
      answer: 'Kadian is an extended-release morphine sulfate medication used for chronic pain management and addiction treatment. It provides 24-hour pain control with once or twice daily dosing. Kadian is suitable for patients who require around-the-clock pain management and are working toward addiction recovery goals under medical supervision.'
    },
    {
      question: 'Do you offer treatment for alcohol addiction?',
      answer: 'Yes, we provide comprehensive alcohol use disorder treatment including medication-assisted treatment options like naltrexone and acamprosate. Our program includes withdrawal management, craving reduction therapy, and coordination with counseling services to support your recovery journey from alcohol addiction.'
    },
    {
      question: 'What smoking cessation options are available?',
      answer: 'Our smoking cessation program offers multiple approaches including nicotine replacement therapy (patches, gum, lozenges), prescription medications like varenicline (Champix) and bupropion (Zyban), and behavioral support strategies. We create personalized quit plans and provide ongoing monitoring to help you successfully quit smoking.'
    },
    {
      question: 'Is my treatment confidential?',
      answer: 'Absolutely. All addiction treatment services are completely confidential and protected by healthcare privacy laws. Your personal health information is secure, and we maintain strict confidentiality for all patients seeking addiction treatment at our Thornhill facility.'
    },
    {
      question: 'Do I need a referral to start treatment?',
      answer: 'For most addiction treatment programs, you will need a referral from a physician. However, we encourage you to call us at (905) 709-9985 to discuss your specific situation. Our team can guide you through the referral process and help you get started on your recovery journey as quickly as possible.'
    },
    {
      question: 'What areas do you serve?',
      answer: 'We proudly serve patients from Thornhill, North York, Richmond Hill, Vaughan, Toronto, and surrounding areas in the Greater Toronto Area. Our convenient Yonge Street location is easily accessible by car and public transit.'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const scrollToFAQ = () => {
    const faqSection = document.getElementById('faq-section');
    if (faqSection) {
      faqSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const navigateToAddictionInfo = () => {
    navigate('/what-is-addiction');
  };

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar isScrolled={isScrolled} />
      
      {/* Slider Hero Section */}
      <section className="relative min-h-[70vh] sm:min-h-screen flex items-center justify-center overflow-hidden">
        {/* Slides */}
        <div className="absolute inset-0">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
            </div>
          ))}
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 w-full px-4 sm:px-6 py-16 sm:py-24">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center justify-center mb-6 sm:mb-8" aria-label="Breadcrumb">
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center text-white/80 hover:text-white transition-colors cursor-pointer"
                  aria-label="Go to homepage"
                >
                  <i className="ri-home-4-line text-base sm:text-lg"></i>
                  <span className="ml-2 text-xs sm:text-sm font-medium">Home</span>
                </button>
                <i className="ri-arrow-right-s-line text-white/60 text-base sm:text-lg"></i>
                <span className="text-white text-xs sm:text-sm font-semibold">Addiction Services</span>
              </div>
            </nav>

            {/* Slide Content */}
            <header className="text-center mb-8 sm:mb-10" key={currentSlide}>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                {slides[currentSlide].title}
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed">
                {slides[currentSlide].description}
              </p>

              {/* Action Buttons - 2 Column Grid */}
              <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto">
                <a
                  href="tel:9057099985"
                  className="bg-primary-600 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-primary-700 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Call Thornhill Drug-Mart at 905-709-9985"
                >
                  <i className="ri-phone-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">(905) 709-9985</span>
                  <span className="sm:hidden">Call Now</span>
                </a>
                <a
                  href="/location"
                  className="bg-white text-gray-900 px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-sm sm:text-base font-semibold hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
                  aria-label="Visit our Thornhill location"
                >
                  <i className="ri-map-pin-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">Visit Us</span>
                  <span className="sm:hidden">Location</span>
                </a>
              </div>
            </header>
          </div>
        </div>

        {/* Navigation Arrows - Desktop */}
        <button
          onClick={prevSlide}
          className="hidden lg:flex absolute left-4 xl:left-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-md hover:bg-white/30 rounded-full items-center justify-center transition-all cursor-pointer group z-20"
          aria-label="Previous slide"
        >
          <i className="ri-arrow-left-s-line text-white text-2xl group-hover:scale-110 transition-transform"></i>
        </button>
        <button
          onClick={nextSlide}
          className="hidden lg:flex absolute right-4 xl:right-8 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-md hover:bg-white/30 rounded-full items-center justify-center transition-all cursor-pointer group z-20"
          aria-label="Next slide"
        >
          <i className="ri-arrow-right-s-line text-white text-2xl group-hover:scale-110 transition-transform"></i>
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex items-center space-x-2 sm:space-x-3 z-20">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`transition-all cursor-pointer ${
                index === currentSlide
                  ? 'w-8 sm:w-10 h-2 bg-white'
                  : 'w-2 h-2 bg-white/50 hover:bg-white/70'
              } rounded-full`}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>

        {/* Scroll Down Arrow */}
        <div className="absolute bottom-16 sm:bottom-20 left-1/2 -translate-x-1/2 z-20 animate-bounce">
          <i className="ri-arrow-down-line text-white text-2xl sm:text-3xl"></i>
        </div>
      </section>

      {/* Addiction Overview Section */}
      <section className="py-16 sm:py-24 bg-primary-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Section Header */}
          <header className="text-center mb-12 sm:mb-16">
            <h2
              className="text-3xl sm:text-4xl md:text-[44px] text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
            >
              An Overview of <em className="not-italic" style={{ fontStyle: 'italic' }}>Addiction</em>
            </h2>
            <p
              className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-white/60 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}
            >
              Evidence-based therapies and assessments to support your recovery journey
            </p>
          </header>

          {/* Four Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {/* Card 1 - Understanding Addiction */}
            <a
              href="/what-is-addiction"
              className="group relative rounded-[24px] overflow-hidden aspect-[3/4] cursor-pointer block"
              aria-label="Learn about understanding addiction"
            >
              <img
                src="https://readdy.ai/api/search-image?query=thoughtful%20woman%20in%20warm%20beige%20sweater%20contemplating%2C%20soft%20cream%20background%2C%20warm%20natural%20lighting%2C%20mental%20health%20awareness%2C%20introspective%20mood%2C%20clean%20minimalist%20aesthetic%2C%20beige%20and%20cream%20tones%2C%20gentle%20shadows%2C%20professional%20healthcare%20photography%20style&width=500&height=650&seq=addiction-overview-v2-1&orientation=portrait"
                alt="Understanding addiction at Thornhill clinic"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
                decoding="async"
              />
              {/* Gradient transparent blur overlay */}
              <div
                className="absolute bottom-0 left-0 right-0 h-[60%] backdrop-blur-md"
                style={{
                  maskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
                  WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
                  background: 'linear-gradient(to top, rgba(22,16,30,0.85) 0%, rgba(22,16,30,0.5) 50%, transparent 100%)'
                }}
              ></div>
              {/* Text content */}
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                <h3
                  className="text-white text-xl sm:text-2xl leading-snug mb-4"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                >
                  Understanding<br />Addiction
                </h3>
                <ul className="space-y-1.5">
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    What is addiction
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    How it affects the brain
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Types of substance use
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    When to seek help
                  </li>
                </ul>
              </div>
            </a>

            {/* Card 2 - Drug Use & Dependency */}
            <a
              href="/drug-use"
              className="group relative rounded-[24px] overflow-hidden aspect-[3/4] cursor-pointer block"
              aria-label="Learn about drug use and dependency"
            >
              <img
                src="https://readdy.ai/api/search-image?query=medical%20pills%20and%20capsules%20on%20soft%20neutral%20surface%2C%20prescription%20medication%20concept%2C%20addiction%20dependency%20awareness%2C%20clean%20minimal%20healthcare%20photography%2C%20warm%20beige%20and%20cream%20tones%2C%20soft%20diffused%20lighting%2C%20professional%20pharmaceutical%20aesthetic&width=500&height=650&seq=addiction-overview-v2-2&orientation=portrait"
                alt="Drug use and dependency awareness"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
                decoding="async"
              />
              {/* Gradient transparent blur overlay */}
              <div
                className="absolute bottom-0 left-0 right-0 h-[60%] backdrop-blur-md"
                style={{
                  maskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
                  WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
                  background: 'linear-gradient(to top, rgba(22,16,30,0.85) 0%, rgba(22,16,30,0.5) 50%, transparent 100%)'
                }}
              ></div>
              {/* Text content */}
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                <h3
                  className="text-white text-xl sm:text-2xl leading-snug mb-4"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                >
                  Drug Use &<br />Dependency
                </h3>
                <ul className="space-y-1.5">
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Opioid dependency
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Prescription drug misuse
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Physical vs. psychological
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Signs of dependency
                  </li>
                </ul>
              </div>
            </a>

            {/* Card 3 - Signs & Assessment */}
            <a
              href="/are-you-addicted"
              className="group relative rounded-[24px] overflow-hidden aspect-[3/4] cursor-pointer block"
              aria-label="Assess signs of addiction"
            >
              <img
                src="https://readdy.ai/api/search-image?query=person%20sitting%20alone%20looking%20thoughtful%20with%20hand%20on%20chin%2C%20self%20reflection%20and%20awareness%2C%20seeking%20help%20for%20addiction%2C%20warm%20neutral%20cream%20background%2C%20soft%20natural%20lighting%2C%20compassionate%20mental%20health%20imagery%2C%20minimalist%20clean%20aesthetic%2C%20beige%20tones&width=500&height=650&seq=addiction-overview-v2-3&orientation=portrait"
                alt="Addiction signs and self assessment"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
                decoding="async"
              />
              {/* Gradient transparent blur overlay */}
              <div
                className="absolute bottom-0 left-0 right-0 h-[60%] backdrop-blur-md"
                style={{
                  maskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
                  WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
                  background: 'linear-gradient(to top, rgba(22,16,30,0.85) 0%, rgba(22,16,30,0.5) 50%, transparent 100%)'
                }}
              ></div>
              {/* Text content */}
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                <h3
                  className="text-white text-xl sm:text-2xl leading-snug mb-4"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                >
                  Signs &<br />Assessment
                </h3>
                <ul className="space-y-1.5">
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Behavioral changes
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Physical symptoms
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Self-assessment tools
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Professional evaluation
                  </li>
                </ul>
              </div>
            </a>

            {/* Card 4 - Treatment Programs */}
            <a
              href="/methadone-treatment"
              className="group relative rounded-[24px] overflow-hidden aspect-[3/4] cursor-pointer block"
              aria-label="Explore treatment programs"
            >
              <img
                src="https://readdy.ai/api/search-image?query=supportive%20healthcare%20professional%20with%20patient%20in%20modern%20clinic%2C%20compassionate%20addiction%20treatment%2C%20warm%20natural%20lighting%2C%20clean%20medical%20environment%20with%20cream%20and%20beige%20tones%2C%20recovery%20and%20hope%2C%20professional%20healthcare%20photography%2C%20minimalist%20aesthetic&width=500&height=650&seq=addiction-overview-v2-4&orientation=portrait"
                alt="Addiction treatment programs at Thornhill"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
                decoding="async"
              />
              {/* Gradient transparent blur overlay */}
              <div
                className="absolute bottom-0 left-0 right-0 h-[60%] backdrop-blur-md"
                style={{
                  maskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
                  WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%)',
                  background: 'linear-gradient(to top, rgba(22,16,30,0.85) 0%, rgba(22,16,30,0.5) 50%, transparent 100%)'
                }}
              ></div>
              {/* Text content */}
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                <h3
                  className="text-white text-xl sm:text-2xl leading-snug mb-4"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
                >
                  Treatment<br />Programs
                </h3>
                <ul className="space-y-1.5">
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Methadone treatment
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Suboxone programs
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Kadian therapy
                  </li>
                  <li className="flex items-start text-white/80 text-[11px] sm:text-xs leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
                    <span className="mr-2 mt-[2px]">&bull;</span>
                    Personalized plans
                  </li>
                </ul>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Treatment Programs - Image Cards with Overlay */}
      <section className="py-12 sm:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-primary-50 px-4 py-2 rounded-full mb-4">
              <span className="text-primary-700 text-sm font-semibold uppercase tracking-wide">Treatment</span>
            </div>
            
            {/* Title */}
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-foreground-950 mb-4">
              <strong>Addiction Treatment</strong> Programs
            </h2>
            
            {/* Description */}
            <p className="text-base sm:text-lg text-foreground-700 max-w-3xl mx-auto leading-relaxed">
              <strong>Addiction</strong> is a treatable condition, and help is available at our <strong>Thornhill addiction pharmacy</strong> and <strong>clinic</strong>. Individuals can find the right path to healing with various <strong>addiction recovery</strong> programs including <strong>methadone pharmacy</strong> services, <strong>Suboxone clinic</strong> programs, and comprehensive support options serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>.
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {treatmentPrograms.map((program, index) => (
              <article 
                key={index}
                className="group bg-background-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all flex flex-col h-full"
              >
                {/* Image with Gradient Overlay - Fixed Height */}
                <div className="relative w-full h-64 overflow-hidden flex-shrink-0">
                  <img
                    src={program.image}
                    alt={`${program.title} at Thornhill addiction pharmacy and clinic`}
                    title={`${program.title} - Thornhill Addiction Services`}
                    className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Gradient Overlay - Dark at bottom */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  
                  {/* Title at Bottom - Fixed Height Container */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-xl font-bold text-white leading-tight line-clamp-2 min-h-[3.5rem]">
                      {program.title}
                    </h3>
                  </div>
                </div>

                {/* Card Body - White Background - Flex Grow */}
                <div className="p-6 sm:p-8 bg-background-50 flex flex-col flex-grow">
                  {/* Description - Fixed Height with Line Clamp */}
                  <div 
                    className="text-sm sm:text-base text-foreground-700 leading-relaxed mb-6 line-clamp-4 flex-grow"
                    dangerouslySetInnerHTML={{ __html: program.description }}
                  />

                  {/* CTA Button - Always at Bottom */}
                  <a
                    href={program.link}
                    className="w-full bg-primary-600 hover:bg-primary-700 text-white py-3 rounded-lg font-semibold transition-all hover:scale-105 cursor-pointer flex items-center justify-center group mt-auto"
                    aria-label={`Learn more about ${program.title}`}
                  >
                    <span className="text-sm sm:text-base">Learn More</span>
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground-950 mb-3 sm:mb-4">
              Why Choose <span className="text-primary-600">Thornhill Drug-Mart</span> <strong>Addiction Pharmacy</strong>
            </h2>
            <p className="text-base sm:text-lg text-foreground-700 max-w-3xl mx-auto">
              Your trusted partner for <strong>addiction recovery</strong> in <strong>Thornhill</strong>, serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, and <strong>North York</strong>
            </p>
          </header>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index}
                className="bg-background-100 rounded-xl p-6 hover:bg-primary-50 transition-all hover:shadow-lg"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-text-primary-600 text-white rounded-lg mb-4">
                  <i className={`${item.icon} text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-foreground-950 mb-2">{item.title}</h3>
                <p className="text-sm sm:text-base text-foreground-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq-section" className="py-12 sm:py-16 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground-950 mb-3 sm:mb-4">
              Frequently Asked <span className="text-primary-600">Questions</span>
            </h2>
            <p className="text-base sm:text-lg text-foreground-700">
              Common questions about our <strong>addiction pharmacy</strong> and <strong>clinic services</strong> in <strong>Thornhill</strong>
            </p>
          </header>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <article 
                key={index}
                className="bg-background-50 rounded-xl shadow-md overflow-hidden"
                itemScope
                itemType="https://schema.org/Question"
              >
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-background-100 transition-colors cursor-pointer"
                  aria-expanded={expandedFaq === index}
                  aria-controls={`faq-answer-${index}`}
                >
                  <h3 
                    className="text-base sm:text-lg font-semibold text-foreground-950 pr-4"
                    itemProp="name"
                  >
                    {faq.question}
                  </h3>
                  <i className={`${expandedFaq === index ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-primary-600 text-2xl flex-shrink-0`}></i>
                </button>
                {expandedFaq === index && (
                  <div 
                    id={`faq-answer-${index}`}
                    className="px-6 pb-5"
                    itemScope
                    itemProp="acceptedAnswer"
                    itemType="https://schema.org/Answer"
                  >
                    <p className="text-sm sm:text-base text-foreground-700 leading-relaxed" itemProp="text">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </article>
            ))}
          </div>
        </div>

        {/* FAQ Schema.org JSON-LD */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })
          }}
        />
      </section>

      {/* Service Areas */}
      <section className="py-12 sm:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <header className="text-center mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground-950 mb-3 sm:mb-4">
              Serving Communities Across the <span className="text-primary-600">Greater Toronto Area</span>
            </h2>
            <p className="text-base sm:text-lg text-foreground-700 max-w-3xl mx-auto">
              Professional <strong>addiction pharmacy</strong> and <strong>clinic services</strong> for patients throughout <strong>Thornhill</strong>, <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, <strong>North York</strong>, and the GTA
            </p>
          </header>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {[
              { name: 'Thornhill', highlight: true },
              { name: 'North York', highlight: false },
              { name: 'Richmond Hill', highlight: false },
              { name: 'Vaughan', highlight: false },
              { name: 'Toronto', highlight: false }
            ].map((area) => (
              <div 
                key={area.name}
                className={`${
                  area.highlight ? 'bg-text-primary-600 text-white' : 'bg-background-100 text-foreground-950'
                } rounded-xl p-6 text-center hover:shadow-lg transition-all`}
              >
                <i className={`ri-map-pin-2-fill ${
                  area.highlight ? 'text-white' : 'text-primary-600'
                } text-3xl mb-3`}></i>
                <p className="text-base sm:text-lg font-bold">{area.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Areas of Focus Section */}
      <section className="py-16 sm:py-24 bg-background-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          {/* Header */}
          <header className="text-center mb-12 sm:mb-16">
            <h2
              className="text-3xl sm:text-4xl md:text-[44px] text-foreground-950 mb-4"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
            >
              Areas of <em className="not-italic" style={{ fontStyle: 'italic' }}>focus</em>
            </h2>
            <p
              className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-gray-500 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}
            >
              Specialized support for your recovery journey
            </p>
          </header>

          {/* Three-column bullet list */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-3 sm:gap-y-2 mb-16 sm:mb-24">
            <ul className="space-y-2 sm:space-y-3">
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/methadone-treatment" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Methadone Maintenance</a>
              </li>
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/suboxone-treatment" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Suboxone Programs</a>
              </li>
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/what-is-addiction" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Kadian Treatment</a>
              </li>
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/what-is-addiction" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Opioid Dependency</a>
              </li>
            </ul>
            <ul className="space-y-2 sm:space-y-3">
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/what-is-addiction" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Alcohol Use Disorder</a>
              </li>
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/pharmacy-services" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Smoking Cessation</a>
              </li>
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/drug-use" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Withdrawal Management</a>
              </li>
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/are-you-addicted" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Relapse Prevention</a>
              </li>
            </ul>
            <ul className="space-y-2 sm:space-y-3">
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/naloxone-kits" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Overdose Prevention</a>
              </li>
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/what-is-addiction" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Chronic Pain Support</a>
              </li>
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/addiction-services" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Confidential Counseling</a>
              </li>
              <li className="flex items-start text-[12px] sm:text-[13px] text-foreground-950 leading-relaxed" style={{ fontFamily: "'Roboto Condensed', sans-serif", fontWeight: 400 }}>
                <span className="mr-2 mt-[2px]">&bull;</span>
                <a href="/addiction-services" className="cursor-pointer hover:underline underline-offset-2 decoration-gray-400 transition-colors">Family Support Resources</a>
              </li>
            </ul>
          </div>

          {/* Quote */}
          <div className="text-center">
            <p
              className="text-2xl sm:text-3xl md:text-[40px] text-foreground-950 leading-tight max-w-3xl mx-auto"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}
            >
              At Thornhill Drug-Mart,
              <br />
              care <em className="not-italic" style={{ fontStyle: 'italic' }}>isn't</em> just about medication,
              <br />
              <em className="not-italic" style={{ fontStyle: 'italic' }}>it's</em> about compassion.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-text-primary-600 to-cyan-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4 sm:mb-6">
            Ready to Start Your <strong>Recovery Journey</strong>?
          </h2>
          <p className="text-base sm:text-lg text-white/90 mb-8 sm:mb-10 leading-relaxed">
            Take the first step toward lasting <strong>addiction recovery</strong> at our <strong>Thornhill addiction pharmacy</strong> and <strong>clinic</strong>. Our compassionate team is here to support you every step of the way with <strong>methadone pharmacy</strong> services, <strong>Suboxone clinic</strong> programs, and comprehensive care. Serving <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, <strong>North York</strong>, and surrounding areas. Call us today for a confidential consultation.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a
              href="tel:9057099985"
              className="bg-white text-primary-600 px-6 sm:px-8 py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-background-200/70 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="Call Thornhill addiction pharmacy at 905-709-9985"
            >
              <i className="ri-phone-line mr-2"></i>
              (905) 709-9985
            </a>
            <a
              href="/location"
              className="bg-primary-700 text-white px-6 sm:px-8 py-4 rounded-lg text-base sm:text-lg font-semibold hover:bg-primary-800 transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg flex items-center justify-center"
              aria-label="Visit Thornhill addiction clinic location"
            >
              <i className="ri-map-pin-line mr-2"></i>
              Visit Our Location
            </a>
          </div>

          <div className="text-center mb-16">
            <a 
              href="/what-is-addiction"
              className="inline-flex items-center gap-2 bg-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Learn About Addiction Treatment
              <i className="ri-arrow-right-line text-xl"></i>
            </a>
          </div>

          <div className="mt-8 sm:mt-10 flex items-center justify-center space-x-2 text-white/80">
            <i className="ri-shield-check-line text-xl"></i>
            <p className="text-sm sm:text-base">All services are completely confidential</p>
          </div>
        </div>
      </section>

      <MobileStickyCTA />
      <Footer />
    </div>
  );
}
